import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Phone, CalendarCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumb, CtaBand } from "@/components/PageBits";
import { useLead } from "@/context/LeadContext";
import { useBooking } from "@/context/BookingContext";
import { AnimatedLines } from "@/components/landing/AnimatedLines";
import { findZone, CATEGORIES, servicesOfCategory, SITE, CONTACT } from "@/data/pages";

export default function ZonePage() {
  const { slug } = useParams();
  const { openForm } = useLead();
  const { openBooking } = useBooking();
  const zone = findZone(slug);
  if (!zone) return <Navigate to="/404" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${SITE.name} — ${zone.city}`,
    telephone: CONTACT.phone,
    areaServed: zone.city,
    url: `${SITE.domain}/${zone.slug}`,
  };

  return (
    <div data-testid="zone-page">
      <Seo title={zone.metaTitle} description={zone.metaDescription} path={`/${zone.slug}`} image={zone.image} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0">
          <img src={zone.image} alt={`Nettoyage à ${zone.city}`} className="w-full h-full object-cover opacity-30" />
        </div>
        <AnimatedLines variant="dark" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-16 lg:py-24">
          <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: `Nettoyage à ${zone.city}` }]} />
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Nettoyage à {zone.city} — Auto, Habitat & Professionnel
          </h1>
          <p className="text-white/80 mt-5 text-base sm:text-lg leading-relaxed max-w-2xl">{zone.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid="zone-book"
              onClick={openBooking}
              className="cta inline-flex items-center gap-2 bg-royal text-white font-extrabold px-7 py-4 rounded-sm hover:bg-royal-hover shadow-lg shadow-royal/30"
            >
              <CalendarCheck className="w-5 h-5" /> Réserver mon créneau
            </button>
            <button
              data-testid="zone-cta"
              onClick={() => openForm("auto")}
              className="cta inline-flex items-center gap-2 border border-white/30 text-white font-bold px-7 py-4 rounded-sm hover:bg-white hover:text-ink"
            >
              Obtenir mon devis <ArrowRight className="w-5 h-5" />
            </button>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/30 font-bold px-7 py-4 rounded-sm hover:bg-white hover:text-ink transition-colors">
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-ink flex items-center gap-2">
          <MapPin className="w-6 h-6" /> Nos prestations à {zone.city}
        </h2>
        <div className="mt-8 space-y-8">
          {CATEGORIES.map((cat) => (
            <div key={cat.slug}>
              <h3 className="font-heading font-bold text-lg text-ink">
                <Link to={`/${cat.slug}`} className="hover:text-muted-foreground transition-colors">{cat.name} à {zone.city}</Link>
              </h3>
              <div className="flex flex-wrap gap-2 mt-3">
                {servicesOfCategory(cat.slug).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    className="text-sm font-semibold bg-marble-light border border-border rounded-sm px-4 py-2 hover:border-ink transition-colors"
                  >
                    {s.name} à {zone.city}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CtaBand onReserve={() => openForm("auto")} onBook={openBooking} title={`Un projet à ${zone.city} ?`} subtitle="Devis sous 24h pour les urgences, 48h pour le reste." />
    </div>
  );
}
